import {useEffect, useMemo, useState} from "react";
import {Controller, FormProvider, useForm} from "react-hook-form";
import {useDispatch, useSelector} from "react-redux";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import {FetchBaseQueryError} from "@reduxjs/toolkit/query";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {Input} from "@pg-design/inputs-module";
import {Loader} from "@pg-design/loader-module";
import {IPhoneFieldValue, PhoneField} from "@pg-design/phone-input-module";
import {Text} from "@pg-design/text-module";
import {notifyBugsnagClient} from "@pg-mono/bugsnag-client";

import {IRPStore} from "../../app/rp_reducer";
import {useCreateFinancialApplicationMutation} from "../../application/api/post_financial_application";
import {
    buildRpFinancialApplicationPayload,
    mapCreditApplicationSourceToFinancialApplicationSource
} from "../../application/utils/build_rp_financial_application_payload";
import {ConsentCheckboxField} from "../../consents/components/ConsentCheckboxField";
import {DefaultConsentNotice} from "../../consents/components/DefaultConsentNotice";
import {useConsentTemplates} from "../../consents/hooks/use_consent_templates";
import {buildConsentRecords} from "../../consents/utils/build_consent_records";
import {toJsonValues} from "../../form/translate/to_json_values";
import {validationMessages} from "../../form/utils/validation_messages";
import {addWarningNotification} from "../../notifications/utils/add_warning_notification";
import {formFields, formValuesType, useCalculatorFormInitialValues} from "../hooks/use_calculator_form_initial_values";
import {ICreditData, ICreditRatingData} from "../types/ICreditData";

type calculatorFormValuesType = formValuesType & {consents: Record<string, boolean>};

const baseValidationShape = {
    email: Yup.string().required(validationMessages.required).email(validationMessages.email),
    name: Yup.string().required(validationMessages.required),
    phone: Yup.object({
        code: Yup.number().required(validationMessages.required),
        number: Yup.string().required(validationMessages.required)
    })
};

interface IProps {
    submitData: ICreditData | ICreditRatingData;
    onAfterSend: (isSuccess: boolean) => void;
}

export const CreditCalculatorForm = (props: IProps) => {
    const codes = useSelector((store: IRPStore) => store.variables.data?.country_codes);
    const initialValues = useCalculatorFormInitialValues();

    const financialApplicationSource = mapCreditApplicationSourceToFinancialApplicationSource(props.submitData.source);
    const {
        defaultConsents,
        optionalConsents,
        mandatoryConsents,
        isLoading: isConsentsLoading,
        isError: isConsentsError
    } = useConsentTemplates(financialApplicationSource);

    const [post, data] = useCreateFinancialApplicationMutation();
    const [sending, setSending] = useState(false);

    const dispatch = useDispatch();

    const consentCheckboxes = useMemo(() => [...optionalConsents, ...mandatoryConsents], [optionalConsents, mandatoryConsents]);

    const validationSchema = useMemo(
        () =>
            Yup.object({
                ...baseValidationShape,
                consents: Yup.object(
                    Object.fromEntries(mandatoryConsents.map((template) => [template.ulid, Yup.boolean().oneOf([true], validationMessages.required)]))
                )
            }),
        [mandatoryConsents]
    );

    const formMethods = useForm<calculatorFormValuesType>({
        defaultValues: {...initialValues, consents: {}},
        resolver: yupResolver(validationSchema)
    });

    const {handleSubmit, control, formState, reset} = formMethods;
    const {errors} = formState;

    useEffect(() => {
        reset((formValues) => ({...formValues, ...initialValues}));
    }, [initialValues]);

    useEffect(() => {
        if (consentCheckboxes.length === 0) {
            return;
        }

        reset((formValues) => ({
            ...formValues,
            consents: Object.fromEntries(consentCheckboxes.map((template) => [template.ulid, formValues.consents[template.ulid] ?? false]))
        }));
    }, [consentCheckboxes]);

    useEffect(() => {
        if (data.status === "rejected") {
            const error = data.error as FetchBaseQueryError;
            notifyBugsnagClient({message: error.status.toString()}, "CreditCalculatorForm useCreateFinancialApplicationMutation error");
            dispatch(addWarningNotification("Twoje zapytanie nie zostało wysłane. Sprawdź podane dane lub spróbuj ponownie później."));
            setSending(false);
        }

        if (data.status === "fulfilled") {
            props.onAfterSend(true);
        }
    }, [data]);

    const onSubmit = (values: calculatorFormValuesType) => {
        setSending(true);
        const parsedValues = toJsonValues(formFields, values);
        const payload = buildRpFinancialApplicationPayload({
            name: parsedValues.name,
            email: parsedValues.email,
            phone: values.phone,
            submitData: props.submitData,
            consents: buildConsentRecords(values.consents, defaultConsents)
        });
        post(payload);
    };

    const renderError = (message?: string) => {
        return (
            <Text variant="info_txt_1" className={errorMessageStyle}>
                {message}
            </Text>
        );
    };

    return (
        <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Controller
                    name="name"
                    control={control}
                    render={({field}) => <Input placeholder="Imię i nazwisko" {...field} fieldState={errors.name && "error"} className={inputMarginStyle} />}
                />
                {errors.name && renderError(errors.name.message)}
                <Controller
                    name="email"
                    control={control}
                    render={({field}) => <Input placeholder="Email" {...field} fieldState={errors.email && "error"} className={inputMarginStyle} />}
                />
                {errors.email && renderError(errors.email.message)}
                <div className={inputMarginStyle}>
                    <Controller
                        name="phone"
                        control={control}
                        render={({field: {onChange, value, name}}) => {
                            const onValueChange = (field: string, value: IPhoneFieldValue) => {
                                onChange(value);
                            };

                            return (
                                <PhoneField
                                    codes={codes || []}
                                    onChange={onValueChange}
                                    onAfterChange={() => null}
                                    value={value}
                                    name={name}
                                    placeholder="Numer telefonu"
                                    fieldState={(errors.phone?.code || errors.phone?.number) && "error"}
                                />
                            );
                        }}
                    />
                </div>
                {errors.phone?.code && renderError(errors.phone.code.message)}
                {errors.phone?.number && renderError(errors.phone.number.message)}

                {consentCheckboxes.map((template) => (
                    <ConsentCheckboxField<calculatorFormValuesType> key={template.ulid} template={template} className={consentCheckboxStyle} />
                ))}

                {isConsentsError && renderError("Nie udało się wczytać zgód. Spróbuj ponownie później.")}

                <Button type="submit" variant="filled_primary" className={buttonStyle} disabled={isConsentsLoading || isConsentsError}>
                    {sending ? <Loader size="sm" /> : "Wyślij"}
                </Button>

                {defaultConsents.map((template) => (
                    <DefaultConsentNotice key={template.ulid} template={template} />
                ))}
            </form>
        </FormProvider>
    );
};

const errorMessageStyle = css({
    color: "var(--colors-danger)",
    width: "100%",
    textAlign: "right",
    marginBottom: 1.5
});

const inputMarginStyle = css({
    marginBottom: 1.5
});

const consentCheckboxStyle = css({
    marginBottom: 1.5,

    "& > div > div": {
        display: "flex",
        alignItems: "flex-start"
    }
});

const buttonStyle = css({
    width: "100%",
    marginBottom: 1.5
});
import {useEffect, useMemo, useState} from "react";
import {Controller, FormProvider, useForm} from "react-hook-form";
import {useDispatch, useSelector} from "react-redux";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import {FetchBaseQueryError} from "@reduxjs/toolkit/query";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {Input} from "@pg-design/inputs-module";
import {Loader} from "@pg-design/loader-module";
import {IPhoneFieldValue, PhoneField} from "@pg-design/phone-input-module";
import {Text} from "@pg-design/text-module";
import {notifyBugsnagClient} from "@pg-mono/bugsnag-client";

import {IRPStore} from "../../app/rp_reducer";
import {useCreateFinancialApplicationMutation} from "../../application/api/post_financial_application";
import {
    buildRpFinancialApplicationPayload,
    mapCreditApplicationSourceToFinancialApplicationSource
} from "../../application/utils/build_rp_financial_application_payload";
import {ConsentCheckboxField} from "../../consents/components/ConsentCheckboxField";
import {DefaultConsentNotice} from "../../consents/components/DefaultConsentNotice";
import {useConsentTemplates} from "../../consents/hooks/use_consent_templates";
import {buildConsentRecords} from "../../consents/utils/build_consent_records";
import {toJsonValues} from "../../form/translate/to_json_values";
import {validationMessages} from "../../form/utils/validation_messages";
import {addWarningNotification} from "../../notifications/utils/add_warning_notification";
import {formFields, formValuesType, useCalculatorFormInitialValues} from "../hooks/use_calculator_form_initial_values";
import {ICreditData, ICreditRatingData} from "../types/ICreditData";

type calculatorFormValuesType = formValuesType & {consents: Record<string, boolean>};

const baseValidationShape = {
    email: Yup.string().required(validationMessages.required).email(validationMessages.email),
    name: Yup.string().required(validationMessages.required),
    phone: Yup.object({
        code: Yup.number().required(validationMessages.required),
        number: Yup.string().required(validationMessages.required)
    })
};

interface IProps {
    submitData: ICreditData | ICreditRatingData;
    onAfterSend: (isSuccess: boolean) => void;
}

export const CreditCalculatorForm = (props: IProps) => {
    const codes = useSelector((store: IRPStore) => store.variables.data?.country_codes);
    const initialValues = useCalculatorFormInitialValues();

    const financialApplicationSource = mapCreditApplicationSourceToFinancialApplicationSource(props.submitData.source);
    const {
        defaultConsents,
        optionalConsents,
        mandatoryConsents,
        isLoading: isConsentsLoading,
        isError: isConsentsError
    } = useConsentTemplates(financialApplicationSource);

    const [post, data] = useCreateFinancialApplicationMutation();
    const [sending, setSending] = useState(false);

    const dispatch = useDispatch();

    const consentCheckboxes = useMemo(() => [...optionalConsents, ...mandatoryConsents], [optionalConsents, mandatoryConsents]);

    const validationSchema = useMemo(
        () =>
            Yup.object({
                ...baseValidationShape,
                consents: Yup.object(
                    Object.fromEntries(mandatoryConsents.map((template) => [template.ulid, Yup.boolean().oneOf([true], validationMessages.required)]))
                )
            }),
        [mandatoryConsents]
    );

    const formMethods = useForm<calculatorFormValuesType>({
        defaultValues: {...initialValues, consents: {}},
        resolver: yupResolver(validationSchema)
    });

    const {handleSubmit, control, formState, reset} = formMethods;
    const {errors} = formState;

    useEffect(() => {
        reset((formValues) => ({...formValues, ...initialValues}));
    }, [initialValues]);

    useEffect(() => {
        if (consentCheckboxes.length === 0) {
            return;
        }

        reset((formValues) => ({
            ...formValues,
            consents: Object.fromEntries(consentCheckboxes.map((template) => [template.ulid, formValues.consents[template.ulid] ?? false]))
        }));
    }, [consentCheckboxes]);

    useEffect(() => {
        if (data.status === "rejected") {
            const error = data.error as FetchBaseQueryError;
            notifyBugsnagClient({message: error.status.toString()}, "CreditCalculatorForm useCreateFinancialApplicationMutation error");
            dispatch(addWarningNotification("Twoje zapytanie nie zostało wysłane. Sprawdź podane dane lub spróbuj ponownie później."));
            setSending(false);
        }

        if (data.status === "fulfilled") {
            props.onAfterSend(true);
        }
    }, [data]);

    const onSubmit = (values: calculatorFormValuesType) => {
        setSending(true);
        const parsedValues = toJsonValues(formFields, values);
        const payload = buildRpFinancialApplicationPayload({
            name: parsedValues.name,
            email: parsedValues.email,
            phone: values.phone,
            submitData: props.submitData,
            consents: buildConsentRecords(values.consents, defaultConsents)
        });
        post(payload);
    };

    const renderError = (message?: string) => {
        return (
            <Text variant="info_txt_1" className={errorMessageStyle}>
                {message}
            </Text>
        );
    };

    return (
        <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Controller
                    name="name"
                    control={control}
                    render={({field}) => <Input placeholder="Imię i nazwisko" {...field} fieldState={errors.name && "error"} className={inputMarginStyle} />}
                />
                {errors.name && renderError(errors.name.message)}
                <Controller
                    name="email"
                    control={control}
                    render={({field}) => <Input placeholder="Email" {...field} fieldState={errors.email && "error"} className={inputMarginStyle} />}
                />
                {errors.email && renderError(errors.email.message)}
                <div className={inputMarginStyle}>
                    <Controller
                        name="phone"
                        control={control}
                        render={({field: {onChange, value, name}}) => {
                            const onValueChange = (field: string, value: IPhoneFieldValue) => {
                                onChange(value);
                            };

                            return (
                                <PhoneField
                                    codes={codes || []}
                                    onChange={onValueChange}
                                    onAfterChange={() => null}
                                    value={value}
                                    name={name}
                                    placeholder="Numer telefonu"
                                    fieldState={(errors.phone?.code || errors.phone?.number) && "error"}
                                />
                            );
                        }}
                    />
                </div>
                {errors.phone?.code && renderError(errors.phone.code.message)}
                {errors.phone?.number && renderError(errors.phone.number.message)}

                {consentCheckboxes.map((template) => (
                    <ConsentCheckboxField<calculatorFormValuesType> key={template.ulid} template={template} className={consentCheckboxStyle} />
                ))}

                {isConsentsError && renderError("Nie udało się wczytać zgód. Spróbuj ponownie później.")}

                <Button type="submit" variant="filled_primary" className={buttonStyle} disabled={isConsentsLoading || isConsentsError}>
                    {sending ? <Loader size="sm" /> : "Wyślij"}
                </Button>

                {defaultConsents.map((template) => (
                    <DefaultConsentNotice key={template.ulid} template={template} />
                ))}
            </form>
        </FormProvider>
    );
};

const errorMessageStyle = css({
    color: "var(--colors-danger)",
    width: "100%",
    textAlign: "right",
    marginBottom: 1.5
});

const inputMarginStyle = css({
    marginBottom: 1.5
});

const consentCheckboxStyle = css({
    marginBottom: 1.5,

    "& > div > div": {
        display: "flex",
        alignItems: "flex-start"
    }
});

const buttonStyle = css({
    width: "100%",
    marginBottom: 1.5
});
